/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
	IInspectorNode_List,
	IInspectorNode_Map,
	isInspectorNodeList,
	TInspectorNode
} from "../../InspectorNodeTypes";
import {
	Breadcrumbs,
	FieldLabel,
	FieldList,
	FieldListBreadcrumb,
	FieldSection,
	FieldSectionContent,
	FieldSectionHeader
} from "@adapptio/lib-editor-ui";
import { IInspectorNodeListItemProps, InspectorNodeListItem } from "../InspectorNodeListItem";
import {
	IExpandedNodesStateItem,
	IInspectorNodeListContext,
	InspectorNodeListContext,
	useInspectorNodeListContext
} from "./InspectorNodeListtContext";
import { isFunction, isNonEmptyArray } from "@adapptio/lib-shared";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { IDragAndDropContextProps } from "../../../ui/components/DragAndDrop/DragAndDropContext";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { InspectorControl } from "../../controls";

import "./InspectorNodeList.scss";
import { useLayoutContext } from "../../../ui/components/Layout/LayoutContext";
import { InspectorNode } from "../InspectorNode";
import { isModel, isSchemaConstArrayModel, isSchemaConstMapModel } from "@adapptio/lib-blueprint";
import { InspectorNodeMapAdd } from "../InspectorNodeMapAdd";
import { useInspectorContext } from "../../InspectorContext";
import { InspectorNodeLabel } from "../InspectorNodeLabel";

export interface IInspectorNodeListProps {
	node: IInspectorNode_List | IInspectorNode_Map;
}

export const InspectorNodeList: React.FC<IInspectorNodeListProps> = ({ node }) => {
	const t = useTranslate();
	const { model } = useBlueprintModel(node.model);

	const inspectorCtx = useInspectorContext();
	const { getLayoutRect } = useLayoutContext();

	const getParentRect = inspectorCtx?.getInspectorRect || getLayoutRect;

	const listRef = useRef<HTMLDivElement>();

	const inspectorNodeListCtx = useInspectorNodeListContext();
	const [ expandedNodesState, setExpandedNodesState ] = useState<IExpandedNodesStateItem[]>([]);

	const depth = (inspectorNodeListCtx?.depth ?? -1) + 1;
	const expandedNodes = inspectorNodeListCtx?.expandedNodes ?? expandedNodesState;
	const expandedNode: IExpandedNodesStateItem = expandedNodes[depth];
	const root = depth === 0;

	const draggable = isSchemaConstArrayModel(model);
	const addInputAllowed = isSchemaConstMapModel(model);

	const offset = useMemo(() => {
		if (!root) {
			return expandedNode?.offset || 0;
		}

		let offset = 0;

		for (let i = 0; i < expandedNodes.length; i++) {
			const curNode = expandedNodes[i];

			if (!curNode) {
				break;
			}

			offset += curNode.offset;
		}

		return offset;
	}, [ root, expandedNodes, expandedNode ]);

	const expandedDepth = useMemo(() => {
		if (expandedNodes.length) {
			const closestClosedIndex = expandedNodes.indexOf(null);

			if (closestClosedIndex > -1) {
				return closestClosedIndex - 1;
			}

			return expandedNodes.length - 1;
		}

		return -1;
	}, [ expandedNodes ]);

	const baseClass = BEM("edt-inspector-node-list", null, {
		expanded: !!expandedNode,
		"last-expanded": expandedNode && !expandedNodes[depth + 1],
		root
	});

	const setExpandedNodes = useCallback<IInspectorNodeListContext["setExpandedNodes"]>(
		(nodes) => {
			if (isFunction(inspectorNodeListCtx?.setExpandedNodes)) {
				inspectorNodeListCtx?.setExpandedNodes(nodes);
				return;
			}

			setExpandedNodesState([ ...nodes ]);
		},
		[ setExpandedNodesState, inspectorNodeListCtx ]
	);

	const toggleExpandedNode = useCallback<IInspectorNodeListContext["toggleExpandedNode"]>(
		(depth, itemNode) => {
			if (isFunction(inspectorNodeListCtx?.toggleExpandedNode)) {
				inspectorNodeListCtx?.toggleExpandedNode(depth, itemNode);
			} else {
				setExpandedNodesState((prevState) => {
					const newState = [ ...prevState ];

					if (newState[depth]?.node?.key === itemNode?.node?.key) {
						newState.length = depth;
					} else {
						newState[depth] = itemNode;
						newState.length = depth + 1;
					}

					return newState;
				});
			}
		},
		[ setExpandedNodesState, inspectorNodeListCtx ]
	);

	const handleNodeExpand = useCallback<IInspectorNodeListItemProps["onExpand"]>(
		(inspectorNode, itemRef) => {
			let offset = expandedNode?.offset || 0;

			if (!offset && isFunction(getParentRect)) {
				const layoutRect = getParentRect();
				const itemRect = itemRef?.current?.getBoundingClientRect();

				offset = itemRect.x - layoutRect.x;
			}

			const nodeState: IExpandedNodesStateItem = {
				node: inspectorNode,
				offset
			};

			toggleExpandedNode(depth, nodeState);
		},
		[ getParentRect, toggleExpandedNode, depth, expandedNode ]
	);

	const handleAdd = useCallback(
		(key: string) => {
			if (!addInputAllowed) {
				return;
			}

			model.schema.addElement(model, key, undefined, true);
		},
		[ model, addInputAllowed ]
	);

	const getRemove = useCallback(
		(node: TInspectorNode) => (index: number) => {
			if (!model) {
				return;
			}

			if (expandedNode?.node?.key === node.key) {
				toggleExpandedNode(depth, expandedNode);
			}

			if (isSchemaConstMapModel(model)) {
				model.schema.removeElement(model, index, true);
			} else {
				model.schema.removeElement(model, index, true);
			}
		},
		[ model, expandedNode, depth, toggleExpandedNode ]
	);

	const handleDragEnd = useCallback<IDragAndDropContextProps["onDragEnd"]>(
		({ destination, source }) => {
			if (!destination || !draggable) {
				return;
			}

			model.schema.moveElement(model, source.index, destination.index, true);
		},
		[ model, draggable ]
	);

	const dataProps = {
		"data-all-expanded-depth": expandedDepth,
		"data-depth": depth
	};

	const controls = isNonEmptyArray(node.controls)
		? node.controls?.map((control) => (
				<InspectorControl key={`${node.key}-${control.type}`} control={control} />
		  ))
		: null;

	const label = t("editor", node.label);

	useLayoutEffect(() => {
		if (!listRef?.current) {
			return;
		}

		listRef.current.style.setProperty("--offset", `${offset}px`);
	}, [ offset ]);

	const isExpanded = useCallback(
		(listItemNode: TInspectorNode) => expandedNode?.node?.key === listItemNode?.key,
		[ expandedNode?.node?.key ]
	);

	const getNodeUpdateRef = useCallback(
		(index: number) =>
			(listItemNode: TInspectorNode): string =>
				`${index}-${isExpanded(listItemNode)}`,
		[ isExpanded ]
	);

	const getNode = useCallback<
		(itemModel: typeof model.items[number], index: number) => () => TInspectorNode
	>(
		(itemModel, index) => () => {
			if (isInspectorNodeList(node)) {
				if (isModel(itemModel)) {
					return node.getChildren(itemModel, index)();
				}

				return null;
			}

			if (!isModel(itemModel)) {
				return node.getChildren(itemModel, index)();
			}
		},
		[ node.key ]
	);

	return (
		<InspectorNodeListContext.Provider
			value={{
				depth,
				toggleExpandedNode,
				expandedNodes,
				setExpandedNodes
			}}
		>
			<FieldSection stateKey={node?.key} defaultCollapsed={false}>
				<FieldSectionHeader controls={controls}>
					{/* <FieldLabel grow> */}
					{label && <InspectorNodeLabel node={node} />}
					{/* <Breadcrumbs>
							<FieldListBreadcrumb
								onClick={() => setExpandedNodes([])}
								disabled={!expandedNodes?.length || !root}
							>
								{label && <InspectorNodeLabel node={node} />}
							</FieldListBreadcrumb>
							{root &&
								expandedNodes?.map(({ node }, index) =>
									node ? (
										<FieldListBreadcrumb
											key={`child-${node?.key}`}
											onClick={() =>
												setExpandedNodes([ ...expandedNodes ].slice(0, index + 1))
											}
											disabled={index === expandedNodes?.length - 1}
										>
											{t("editor", node.label)}
										</FieldListBreadcrumb>
									) : null
								)}
						</Breadcrumbs> */}
					{/* </FieldLabel> */}
				</FieldSectionHeader>
				<FieldSectionContent disablePadding>
					<FieldList
						draggable={draggable}
						className={baseClass.toClassName()}
						{...dataProps}
						onDragEnd={handleDragEnd}
						listRef={listRef}
					>
						{node.model.items.map((itemModel, index) => (
							<InspectorNode
								key={index}
								getNode={getNode(itemModel, index)}
								getUpdateRef={getNodeUpdateRef(index)}
							>
								{(childNode) => {
									return (
										<InspectorNodeListItem
											key={childNode?.key}
											index={index}
											node={childNode}
											draggable={draggable}
											onExpand={handleNodeExpand}
											expanded={isExpanded(childNode)}
											onDelete={getRemove(childNode)}
										/>
									);
								}}
							</InspectorNode>
						))}
						{addInputAllowed && <InspectorNodeMapAdd onSubmit={handleAdd} />}
					</FieldList>
				</FieldSectionContent>
			</FieldSection>
		</InspectorNodeListContext.Provider>
	);
};
